/*
  Вкладка «Проживающие» карточки счёта (20.10.2026): домовая книга - кто зарегистрирован
  или проживает в помещении. Она печатается в финансово-лицевом счёте, который идёт в
  суд приложением к заявлению.

  Строки приходят из учётной системы, из файла и сверенного скана выписки или вносятся
  руками; источник виден у каждой строки словами. Строку, внесённую или исправленную
  человеком, обмен с 1С не стирает; строка из учётной системы вернётся при следующем
  обмене, даже если её убрать, - окно удаления говорит об этом прямо.
*/

import { useCallback, useState } from 'react'

import { api } from '../../api/client.js'
import { date } from '../../api/format.js'
import { Empty, Loading, Notice, Tag } from '../Common.jsx'
import PageBar from '../PageBar.jsx'
import usePagedList from '../usePagedList.js'
import RemoveDialog from './RemoveDialog.jsx'
import ResidentDialog from './ResidentDialog.jsx'

export default function ResidentsTab({ accountId, canEdit, onDone }) {
  const load = useCallback((window) => api.residents(accountId, window), [accountId])
  // Домовая книга большого помещения с историей выбывших - десятки строк; с 21.10.2026
  // страницы режет сервер (требование владельца), здесь - общая панель.
  const { rows, error, reload, bar } = usePagedList(load)
  const [dialog, setDialog] = useState(null)

  const saved = (text) => () => {
    reload()
    onDone?.(text)
  }

  return (
    <section className="section card">
      <div className="row row--spread">
        <h2 className="section__title">Проживающие</h2>
        {canEdit ? (
          <button className="button" type="button" onClick={() => setDialog({ kind: 'add' })}>
            Добавить проживающего
          </button>
        ) : null}
      </div>

      <Notice error={error} />
      {rows === null && !error ? <Loading what="Загружаем домовую книгу" /> : null}
      {rows !== null && rows.length === 0 ? (
        <Empty title="Домовой книги пока нет">
          Её приносит выгрузка, сверенный скан выписки из домовой книги или внесение вручную
          кнопкой выше.
        </Empty>
      ) : null}

      {rows !== null && rows.length > 0 ? (
        <table className="table">
          <thead>
            <tr>
              <th>ФИО</th>
              <th>Дата рождения</th>
              <th>Прибыл</th>
              <th>Выбыл</th>
              <th>Родство</th>
              <th>Источник</th>
              {canEdit ? <th /> : null}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.resident_id}>
                <td>
                  {row.full_name}
                  {row.is_registered ? null : <div className="muted">только проживает</div>}
                </td>
                <td data-label="Дата рождения">{date(row.birth_date)}</td>
                <td data-label="Прибыл">{date(row.arrived_on)}</td>
                <td data-label="Выбыл">{row.left_on ? date(row.left_on) : 'проживает'}</td>
                <td data-label="Родство">{row.relation ?? '-'}</td>
                <td data-label="Источник">
                  <Tag kind={row.source === 'manual' ? 'calm' : 'muted'}>{row.source_title}</Tag>
                </td>
                {canEdit ? (
                  <td data-label="Действия">
                    <div className="row row--tight">
                      <button
                        className="button button--quiet"
                        type="button"
                        onClick={() => setDialog({ kind: 'edit', resident: row })}
                      >
                        Изменить
                      </button>
                      <button
                        className="button button--quiet"
                        type="button"
                        onClick={() => setDialog({ kind: 'remove', resident: row })}
                      >
                        Убрать
                      </button>
                    </div>
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
      <PageBar {...bar} what="проживающих" />

      {dialog?.kind === 'add' || dialog?.kind === 'edit' ? (
        <ResidentDialog
          accountId={accountId}
          resident={dialog.resident}
          onSaved={saved(dialog.kind === 'add' ? 'Проживающий внесён.' : 'Строка исправлена.')}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog?.kind === 'remove' ? (
        <RemoveDialog
          title="Убрать из домовой книги"
          action="Убрать строку"
          onRemove={() => api.removeResident(accountId, dialog.resident.resident_id)}
          onDone={saved('Строка убрана.')}
          onClose={() => setDialog(null)}
        >
          {dialog.resident.full_name} будет убран из домовой книги счёта.
          {dialog.resident.source === 'manual'
            ? ''
            : ' Строка пришла из учётной системы или файла: следующий обмен может вернуть её.'}
        </RemoveDialog>
      ) : null}
    </section>
  )
}
